import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { adminApi } from '../services/api'
import { Loader } from '../components/UI'
import { formatToman, formatDateTimeFa, toFaDigits } from '../utils/format'
import toast from 'react-hot-toast'

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-gold-100 text-gold-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-red-100 text-red-800'
}

const STATUS_FA: Record<string, string> = {
  PENDING: 'در انتظار',
  CONFIRMED: 'تایید شده',
  PROCESSING: 'در حال پردازش',
  COMPLETED: 'تکمیل شده',
  CANCELLED: 'لغو شده'
}

const totalGrams = (order: any) =>
  (order.items || []).reduce(
    (sum: number, i: any) => sum + (i.product?.weight || 0) * i.quantity,
    0
  )

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [updating, setUpdating] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminApi.getOrders({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        page,
        limit: 20
      })
      if (res.data.success && res.data.data) {
        setOrders(res.data.data.orders ?? [])
        setTotalPages(res.data.data.pagination?.totalPages ?? 1)
      }
    } catch {
      toast.error('خطا در دریافت سفارش‌ها')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, typeFilter, page])

  useEffect(() => {
    load()
  }, [load])

  const changeStatus = async (id: string, status: string) => {
    setUpdating(id)
    try {
      const res = await adminApi.updateOrderStatus(id, status)
      if (res.data.success && res.data.data) {
        toast.success('وضعیت سفارش تغییر کرد')
        setOrders((prev) => prev.map((o) => (o.id === id ? res.data.data! : o)))
      } else {
        toast.error(res.data.message || 'خطا')
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'خطا در تغییر وضعیت')
    } finally {
      setUpdating(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">سفارش‌ها</h1>
          <p className="text-sm text-gray-500 mt-1">
            مدیریت سفارش‌های خرید و فروش مشتریان همراه با جزئیات کامل هر معامله
          </p>
        </div>
        <Link to="/trades" className="btn btn-outline">نمای دو ستونه خرید و فروش</Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <select className="input !w-auto" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}>
          <option value="">همه نوع‌ها</option>
          <option value="BUY">خرید مشتری (فروش ما)</option>
          <option value="SELL">فروش مشتری (خرید ما)</option>
        </select>
        <select className="input !w-auto" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}>
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(STATUS_FA).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader /></div>
      ) : (
        <div className="card overflow-hidden">
          <div className="table-wrap">
            <table className="table-admin">
              <thead>
                <tr>
                  <th>شماره</th>
                  <th>مشتری</th>
                  <th>نوع</th>
                  <th>اقلام سفارش</th>
                  <th className="!text-center">تعداد</th>
                  <th className="!text-center">وزن (گرم)</th>
                  <th>مبلغ کل</th>
                  <th>تاریخ</th>
                  <th>وضعیت</th>
                  <th>تغییر وضعیت</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/orders/${o.id}`} className="font-bold text-primary-600 hover:underline" dir="ltr">{o.orderNumber}</Link>
                    </td>
                    <td>
                      <div className="font-medium">{o.user?.firstName} {o.user?.lastName}</div>
                      <div className="text-xs text-gray-400" dir="ltr">{o.user?.email}</div>
                    </td>
                    <td>
                      <span className={`badge ${o.type === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-gold-100 text-gold-800'}`}>
                        {o.type === 'BUY' ? 'خرید مشتری' : 'فروش مشتری'}
                      </span>
                    </td>
                    <td>
                      <div className="text-xs text-gray-700 leading-5">
                        {(o.items || []).map((i: any) => (
                          <div key={i.id}>
                            {i.product?.name} × {toFaDigits(i.quantity)}
                            <span className="text-gray-400"> ({formatToman(i.unitPrice)})</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="text-center font-bold">{toFaDigits(o.items?.length || 0)}</td>
                    <td className="text-center font-bold">{toFaDigits(totalGrams(o))}</td>
                    <td className="font-black whitespace-nowrap">{formatToman(o.total)}</td>
                    <td className="text-xs text-gray-500 whitespace-nowrap">{formatDateTimeFa(o.createdAt)}</td>
                    <td>
                      <span className={`badge ${STATUS_STYLE[o.status] || 'bg-gray-100 text-gray-700'}`}>{STATUS_FA[o.status] || o.status}</span>
                    </td>
                    <td>
                      <select
                        className="input !w-auto !py-1 !text-xs"
                        value={o.status}
                        disabled={updating === o.id || o.status === 'CANCELLED' || o.status === 'COMPLETED'}
                        onChange={(e) => changeStatus(o.id, e.target.value)}
                      >
                        {Object.entries(STATUS_FA).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4">
              <button className="btn btn-secondary !py-1.5" disabled={page <= 1} onClick={() => setPage(page - 1)}>قبلی</button>
              <span className="text-sm">{toFaDigits(page)} از {toFaDigits(totalPages)}</span>
              <button className="btn btn-secondary !py-1.5" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>بعدی</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default Orders
